/**
 * One entry per case list.
 *
 * Everything that used to differ between the six near-identical page
 * files lives here: what the API is asked for, what the page is called,
 * and what to say when the list is empty. CaseListPage does the rest.
 *
 *   type            value sent to user-case-management/get-cases
 *   headers         title + breadcrumb for HeaderComponent
 *   description     one line under the title
 *   emptyState      props for BaseEmpty when the list comes back empty
 *   timeField       which timestamp the Updated column shows (default created_at)
 *   showOwnership   include the Doctor column
 *   showMineLink    show the "Your cases" button in the toolbar
 *   showOverdueFilter  admin-only "Overdue" toggle in the toolbar
 *   onHoldFlag      render "On hold" when refine_id === id
 *   onEditComponent factory returning the edit dialog, if rows are editable
 *   showCreateButton  show the New case button in the page header (Super Admin / Doctor only)
 */

const crumb = (name) => [{ name: "Cases", path: "/cases/total" }, { name }];

export const totalCasesConfig = {
  type: "total",
  module: "cases",
  title: "Total Cases",
  headers: { title: "Total cases", breadcrumb: crumb("Total") },
  description: "Every case you have access to, newest activity first.",
  showMineLink: true,
  // Every case an admin can see lives here, so this is the one list
  // worth narrowing to the ones nobody has picked up.
  showOverdueFilter: true,
  emptyState: {
    icon: "tabler:folders",
    title: "No cases yet",
    description: "Cases appear here as soon as a clinic submits one.",
  },
};

export const newCasesConfig = {
  type: "new",
  module: "cases",
  title: "New Cases",
  headers: { title: "New cases", breadcrumb: crumb("New") },
  description: "Everything that came in today, until it is completed.",
  // A case starts life here, so this is where it is made.
  showCreateButton: true,
  emptyState: {
    icon: "tabler:inbox",
    title: "Nothing new today",
    description: "No case has come in today yet. Older cases are in Total cases.",
  },
};

export const modificationCasesConfig = {
  type: "modifications",
  module: "cases",
  title: "Modification Cases",
  headers: { title: "Modifications", breadcrumb: crumb("Modifications") },
  description: "Cases sent back with changes requested.",
  emptyState: {
    icon: "tabler:edit-circle",
    title: "No changes requested",
    description: "Cases a doctor sends back for changes show up here.",
  },
};

export const approvedCasesConfig = {
  type: "approved",
  module: "cases",
  title: "Approved Cases",
  headers: { title: "Approved cases", breadcrumb: crumb("Approved") },
  description: "Treatment plans signed off by the doctor, most recently approved first.",
  // Approving a case is what last saved it, so updated_at is when it
  // was approved — the list is sorted by it on the server too.
  timeField: "updated_at",
  emptyState: {
    icon: "tabler:circle-check",
    title: "Nothing approved yet",
    description: "Cases move here once a doctor signs off the treatment plan.",
  },
};

export const refinementCasesConfig = {
  type: "refinements",
  module: "cases",
  title: "Refinement Cases",
  headers: { title: "Refinements", breadcrumb: crumb("Refinements") },
  description: "Cases back for a refinement round.",
  emptyState: {
    icon: "tabler:refresh-dot",
    title: "No refinements open",
    description: "A case submitted as a refinement of an earlier one shows up here.",
  },
};

export const onHoldCasesConfig = {
  type: "onhold",
  module: "cases",
  title: "On Hold Cases",
  headers: { title: "On hold", breadcrumb: crumb("On hold") },
  description: "Cases a technician sent back because the submitted data does not meet the requirements.",
  onHoldFlag: true,
  emptyState: {
    icon: "tabler:player-pause",
    title: "Nothing on hold",
    description: "A case a technician cannot work with lands here until its doctor fixes the data.",
  },
};

export const mineCasesConfig = {
  type: "mine",
  module: "cases",
  title: "Your Cases",
  headers: { title: "Your cases", breadcrumb: crumb("Yours") },
  description: "Cases you have taken and are working on.",
  showOwnership: false,
  emptyState: {
    icon: "tabler:user-check",
    title: "You haven't taken a case yet",
    description: "Open New cases and take one to start working on it.",
  },
};
